'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useState, useMemo, Fragment } from 'react';

import { useTheme } from 'styled-components';
import dynamic from 'next/dynamic';
import { useParams, useSearchParams } from 'next/navigation';

import useRouterNext from '@hooks/useRouterNext';
import { useSearchURL } from '@api/data-loaders/resource';
import { PackageTransferViewModel, WebUrlType } from '@api/v3';
import { useFiltersContext } from '@contexts/Filters';
import HotelCardSkeleton from '@components/views/hotel/listing/components/ListingSkeleton/HotelCardSkeleton';
import {
  ListWrapper,
  ItemsRowWrapper,
  ListingItemContainer,
  SkeletonItemsWrapper,
  ListingItemCardWrapper,
  ListingCampaignBanner,
  SearchWrapperHidden,
} from '@components/views/hotel/listing/HotelListingStyle';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import HeroCampaignBanner from '@components/ui/banners/hero-campaign-banner/HeroCampaignBanner';
import SeoText from '@components/views/landing/components/seoText/seoText';
import IsInViewPort from '@components/ui/IsInViewPort';
import usePagination from '@components/views/hooks/usePagination';
import { HOTEL_SEARCH_TYPES, HOTEL_SORT_DATA } from '@constants/constants';
import { FlexibleDiv } from '@components/formStyles';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { ListingMetaViewModel } from '@components/views/types';
import { paramsToObject } from '@components/views/utils';
import Pagination from '@components/Pagination/next/Pagination';
import { getLoyaltyTypeFromUtmSource } from '@components/ui/loyalty-verifications/helpers';
import { useDevice } from '@contexts/device';
import clean from '@utils/cleanObject';
import { useKocAliemContext } from '@contexts/loyalty/KocAilem';
import { getListingChatGPTMessage } from '@components/ChatGPTLoader/utils';
import { useSetChatGPTMessage } from '@components/ChatGPTLoader/hooks';
import Typography from '@components/Typography';
import { getHotelSearchURLType } from '@components/views/hotel/utils';
import {
  BreadcrumbWrapper,
  HotelTitle,
  HotelTitleMobile,
} from '@components/views/hotel/detail/HotelDetailStyle';
import SortingTabs from '@components/views/hotel/components/SortingTabs';
import KocAilemBanner from '@components/views/hotel/listing/components/KocAilem/KocAilemBanner';
import { InfoIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { useCampaignBanners } from '@components/views/hooks/useCampaignBanners';
import { packageListAndClick } from '@utils/newDataLayer/productListAndClick';
import { BreadCrumbHotelListSchemaOrg } from '@utils/schemaorg/BreadCrumbHotelListSchemaOrg';
import { SessionScopeGTM } from '@utils/gtm/session-scope';

import { getDataProps } from '../../common/propsGetters';
import { useListingFetching } from './hooks/useListingFetching';
import FlightTransferSelectionComponent from '../components/FlightTransferSelectionComponent';
import ListingItem from './listing-item';
import PackageSearchBar from './search-bar';
import CyprusTab from '../detail/components/package-hotel-selection-tab';
import { ResearchContainer, ResearchWrapper } from './styled';
import PackageResponsiveListingBox from '../detail/components/package-responsive-search-bar/packageResponsiveListingBox';
import HotelSearchBar from './hotel-tab-search-bar';
import ResponsiveListingBox from './hotel-tab-search-bar/ResponsiveListingBox.tsx';
import { FlightProps } from '../components/helper';
const ScrollToTop = dynamic(
  () => import('@components/ScrollToTop/ScrollToTop')
);

const LoyaltyController = dynamic(
  () => import('@components/ui/loyalty-verifications/LoyaltyController')
);

const NotFoundWrapper = dynamic(() =>
  import('@components/views/hotel/listing/HotelListingStyle').then(
    ({ NotFoundWrapper }) => NotFoundWrapper
  )
);

const NotFound = dynamic(() => import('@components/Errors/404/404'));

const ListingFilters = dynamic(
  () =>
    import('@components/views/hotel/listing/components/Filters/ListingFilters')
);
const GeneralLoader = dynamic(() => import('@components/ui/GeneralLoader'));
const PackageListing = ({ metaInfo, initialData }) => {
  const { palette } = useTheme();
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [adultCount, setAdultCount] = useState<number>(0);
  const [childCount, setChildCount] = useState<number>(0);
  const [selectedDepatureFlight, setSelectedDepatureFlight] =
    useState<FlightProps>();
  const [selectedReturnFlight, setSelectedReturnFlight] =
    useState<FlightProps>();
  const [selectedDepatureTransfer, setSelectedDepatureTransfer] =
    useState<PackageTransferViewModel>();
  const [selectedReturnTransfer, setSelectedReturnTransfer] =
    useState<PackageTransferViewModel>();

  const { isDesktop, isTablet, isMobile } = useDevice();
  const { setSort, sort, setFilters } = useFiltersContext();

  const router = useRouterNext();
  const params = useParams();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const loyaltyType = getLoyaltyTypeFromUtmSource(query?.utm_source as string);
  const urlData = useSearchURL();
  const {
    location,
    activeLocation,
    hotelRoomsList,
    startDate,
    endDate,
    setDates,
    setIsFetchedItems,
    isStickySearch,
    setHotelDateSelection,
    isHotelDateSelected,
    setLocation,
  } = useSearchBarContext();
  const [initialRender, setInitialRender] = useState(true);
  const [isAirportSelectionClicked, setIsAirportSelectionClicked] =
    useState(false);
  const [isAirportDateSelectionStarted, setIsAirportDateSelectionStarted] =
    useState(false);

  const [isSearchedFromSearchBar, setIsSearchedFromSearchBar] = useState(false);
  const [selectedTab, setSelectedTab] = useState('package');
  const [packageTabChanged, setPackageTabChanged] = useState(false);

  SessionScopeGTM('Listeleme Sayfası', 'LP - Kıbrıs Otel');
  useEffect(() => {
    if (!initialRender) {
      if (selectedTab === 'hotel') {
        setPackageTabChanged(true);
        setLocation({
          id: 0,
          text: '',
          url: '',
        });
      }
    } else {
      setInitialRender(false);
    }
  }, [selectedTab]);

  const [currentPage, setCurrentPage] = useState(1);

  const {
    isError,
    hotels,
    isFetching,
    isListingSuccess,
    refetch,
    filters: hotelFilters,
    meta = {} as ListingMetaViewModel,
    flights,
    transfers,
  } = useListingFetching({
    setAdultCount,
    setChildCount,
    setDates,
    setFilters,
    setIsFetchedItems,
    initialData,
    packageTabChanged,
  });
  const { campaignBanner } = useCampaignBanners('hotel', 'top');

  useEffect(() => {
    if (typeof window !== 'undefined' && meta) {
      setCurrentPage(query.page ? parseInt(query.page.toString()) : 1);
    }
  }, [query]);

  // ============================
  // Handle set the chatGPT message
  // ============================
  useSetChatGPTMessage(getListingChatGPTMessage('Hotel'));

  // ===================================================
  // Handle the sortBy click and fill the sortBy on page load
  // ===================================================
  useEffect(() => {
    if (query.sort) {
      const targetSort = HOTEL_SORT_DATA?.find(
        (item) => item.name === (query.sort as string)
      );
      if (targetSort) setSort(targetSort);
    } else {
      const defaultSort = HOTEL_SORT_DATA?.find(
        (item) => item.name === 'popularity'
      );
      setSort(defaultSort);
    }
  }, [query]);

  useEffect(() => {
    hotels &&
      hotels?.length > 0 &&
      //@ts-ignore
      hotels[0]?.length > 0 &&
      packageListAndClick(
        hotels[0],
        query.in,
        query.out,
        adultCount,
        childCount,
        sort,
        hotelFilters,
        query,
        false
      );
  }, [hotels]);

  const handleTabClick = (activeKey: string): void => {
    setIsRedirecting(true);
    const item = HOTEL_SORT_DATA?.find((item) => item.id === activeKey);

    const currentSortItem = query?.sort ?? 'popularity';

    const { ...nextQuery } = query;

    if (item?.name === currentSortItem) {
      if (currentSortItem === 'popularity') {
        setIsRedirecting(false);
      }
      router.push({
        pathname: `/${params?.slug.toString()}`,
        query: clean({ ...nextQuery, sort: 'popularity' }),
      });
      return;
    }

    if (item) {
      setSort(item);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      router.push({
        pathname: `/${params?.slug?.toString() ?? ''}`,
        query: { ...nextQuery, sort: item.name },
      });
    }
  };

  const { isValidated: isKocAilemValidate } = useKocAliemContext();

  // ============================
  // Handle Pagination change
  // ============================
  usePagination(currentPage);

  const bannerImage = isDesktop
    ? campaignBanner?.images?.[0]?.url
    : campaignBanner?.responsiveImages?.[0]?.url;
  // ======================================
  // Render Hero Campaigns Banner
  // ======================================
  const renderHeroCampaigns = () => (
    <ListingCampaignBanner>
      <HeroCampaignBanner
        listing={true}
        data={getDataProps(!!isDesktop, palette.eclipse, campaignBanner)}
        images={bannerImage}
        pageName={meta.title + ' Listing Page'}
      />
    </ListingCampaignBanner>
  );

  // ======================================
  // should shallow push if we the target page is hotel listing
  // ======================================
  const shouldShallowPush = useMemo(() => {
    return (
      location.type === WebUrlType.PACKAGE_CITY ||
      location.type === WebUrlType.PACKAGE_REGION ||
      location.type === WebUrlType.PACKAGE_THEME
    );
  }, [location]);

  const toggleDateSelectionModal = () => {
    setIsAirportSelectionClicked(true);
    setIsAirportDateSelectionStarted(true);
  };

  const hotelSliceCount = isMobile || isTablet ? 6 : 4;

  const actionPageLocationType = activeLocation?.type ?? urlData?.type;
  const actionPageLocationLabel = activeLocation?.text ?? urlData?.text;
  const [isRedirecting, setIsRedirecting] = useState(false);

  // ======================================
  // Generate the breadcrumb links
  // ======================================

  const breadItems = useMemo(() => {
    const itemTitle = meta?.title?.split('|')[0];

    return [
      {
        title: 'Ana Sayfa',
        url: '/',
      },
      !isError
        ? {
            title: itemTitle,
            url: `/${params?.slug}`,
          }
        : {},
    ];
  }, [meta?.title, params, isError]);
  useEffect(() => {
    if (isListingSuccess && !isFetching) {
      setIsRedirecting(false);
    }
  }, [isListingSuccess, isFetching, query]);
  const updateRoomParamInUrl = (itemUrl: string): string => {
    const url = new URL(itemUrl ?? '', process.env.NEXT_PUBLIC_HOST);
    const roomParams = url.searchParams.getAll('room');
    if (roomParams.length > 0) {
      const updatedRoomParams = roomParams
        .map((param) => param.replace(/,/g, '_'))
        .join(',');
      url.searchParams.delete('room');
      url.searchParams.append('room', updatedRoomParams);
    } else {
      const updatedRoomParams = '2';
      url.searchParams.append('room', updatedRoomParams);
    }
    return url.toString().replace(process.env.NEXT_PUBLIC_HOST ?? '', '');
  };

  return (
    <>
      {breadItems?.length ? (
        <BreadCrumbHotelListSchemaOrg
          key={actionPageLocationLabel}
          actionPageLocationLabel={actionPageLocationLabel}
        />
      ) : null}
      {isDesktop || isTablet ? <ScrollToTop /> : null}
      <ListWrapper>
        {isDesktop ? (
          <aside>
            <ListingFilters
              setIsRedirecting={setIsRedirecting}
              isExpediaHotels={false}
              hotelFilters={hotelFilters}
              isHotelsLoading={isFetching}
              hotelsCount={selectedTab === 'hotel' ? 0 : meta?.totalCount ?? 0}
              filterRegionText={
                selectedTab === 'hotel' ? '' : activeLocation?.text ?? ''
              }
              isError={isError}
            />
          </aside>
        ) : null}
        <main>
          <ItemsRowWrapper>
            {isDesktop ? (
              <>
                {isStickySearch ? <SearchWrapperHidden /> : null}
                {actionPageLocationType ? (
                  <>
                    {getHotelSearchURLType(actionPageLocationType ?? '') ===
                    HOTEL_SEARCH_TYPES.HOTEL_LISTING ? (
                      <HotelTitle>
                        <Typography variant='Title5' weight='SemiBold'>
                          {`${actionPageLocationLabel} Otelleri`}
                        </Typography>
                      </HotelTitle>
                    ) : (
                      <HotelTitle>
                        <Typography variant='Title5' weight='SemiBold'>
                          {actionPageLocationLabel}
                        </Typography>
                      </HotelTitle>
                    )}
                  </>
                ) : null}
                <BreadcrumbWrapper>
                  <Breadcrumb
                    setIsRedirecting={setIsRedirecting}
                    items={
                      activeLocation?.type === WebUrlType.PACKAGE_THEME
                        ? [
                            {
                              title: 'Ana Sayfa',
                              url: '/',
                            },
                            {
                              title: activeLocation?.text,

                              url: `/${params?.slug}`,
                            },
                          ]
                        : breadItems
                    }
                  />
                </BreadcrumbWrapper>
                <CyprusTab
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
                {selectedTab === 'package' ? (
                  <PackageSearchBar
                    setIsRedirecting={setIsRedirecting}
                    setIsFetchedItems={setIsFetchedItems}
                    setIsSearchedFromSearchBar={setIsSearchedFromSearchBar}
                    isSearchedFromSearchBar={isSearchedFromSearchBar}
                    shouldShallowPush={shouldShallowPush}
                    isListing={true}
                    packageTabChanged={packageTabChanged}
                    setPackageTabChanged={setPackageTabChanged}
                    isAirportSelectionClicked={isAirportSelectionClicked}
                    setIsAirportSelectionClicked={setIsAirportSelectionClicked}
                    isAirportDateSelectionStarted={
                      isAirportDateSelectionStarted
                    }
                    setisAirportDateSelectionStarted={
                      setIsAirportDateSelectionStarted
                    }
                    isHoliday={false}
                  />
                ) : null}
                {selectedTab === 'hotel' ? (
                  <HotelSearchBar
                    setIsRedirecting={setIsRedirecting}
                    setIsFetchedItems={setIsFetchedItems}
                    setIsSearchedFromSearchBar={setIsSearchedFromSearchBar}
                    isSearchedFromSearchBar={isSearchedFromSearchBar}
                    shouldShallowPush={false}
                    selectedTab={selectedTab}
                  />
                ) : null}

                {packageTabChanged ||
                isFetching ||
                isRedirecting ||
                hotels?.[0]?.length === 0 ||
                flights?.length === 0 ||
                isError ? null : (
                  <FlightTransferSelectionComponent
                    adultCount={adultCount}
                    childCount={childCount}
                    transferItems={transfers}
                    flightItems={flights}
                    selectedDepatureFlight={selectedDepatureFlight}
                    selectedReturnFlight={selectedReturnFlight}
                    selectedDepatureTransfer={selectedDepatureTransfer}
                    selectedReturnTransfer={selectedReturnTransfer}
                    setSelectedDepatureFlight={setSelectedDepatureFlight}
                    setSelectedDepatureTransfer={setSelectedDepatureTransfer}
                    setSelectedReturnFlight={setSelectedReturnFlight}
                    setSelectedReturnTransfer={setSelectedReturnTransfer}
                    isListing={true}
                  />
                )}
              </>
            ) : null}

            {isMobile || isTablet ? (
              <>
                {actionPageLocationType ? (
                  <>
                    {getHotelSearchURLType(actionPageLocationType) ===
                    HOTEL_SEARCH_TYPES.HOTEL_LISTING ? (
                      <HotelTitleMobile>
                        <Typography variant='caption' weight='SemiBold'>
                          {actionPageLocationLabel} Otelleri Fiyatları ve
                          Rezervasyon Fırsatları
                        </Typography>
                      </HotelTitleMobile>
                    ) : (
                      <HotelTitleMobile>
                        <Typography variant='caption' weight='SemiBold'>
                          {actionPageLocationLabel}
                        </Typography>
                      </HotelTitleMobile>
                    )}
                  </>
                ) : null}
                <BreadcrumbWrapper>
                  <Breadcrumb
                    setIsRedirecting={setIsRedirecting}
                    items={
                      activeLocation?.type === WebUrlType.PACKAGE_THEME
                        ? [
                            {
                              title: 'Ana Sayfa',
                              url: '/',
                            },
                            {
                              title: activeLocation?.text,

                              url: `/${params?.slug}`,
                            },
                          ]
                        : breadItems
                    }
                  />
                </BreadcrumbWrapper>
                {selectedTab === 'package' ? (
                  <PackageResponsiveListingBox
                    setIsRedirecting={setIsRedirecting}
                    detail={false}
                    startDate={startDate}
                    endDate={endDate}
                    hotelRoomsList={hotelRoomsList}
                    isModalVisible={isShowModal || isHotelDateSelected}
                    toggleIsModalVisible={() => setIsShowModal((val) => !val)}
                    hideModal={() => {
                      setIsShowModal(false);
                      setHotelDateSelection(false);
                    }}
                    meta={meta}
                    location={location}
                    shouldShallowPush={shouldShallowPush}
                    packageTabChanged={packageTabChanged}
                    setPackageTabChanged={setPackageTabChanged}
                    hotelFilters={hotelFilters}
                    isFetching={isFetching}
                    hotels={hotels}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    isAirportSelectionClicked={isAirportSelectionClicked}
                    setIsAirportSelectionClicked={setIsAirportSelectionClicked}
                    isAirportDateSelectionStarted={
                      isAirportDateSelectionStarted
                    }
                    isHoliday={false}
                    setisAirportDateSelectionStarted={
                      setIsAirportDateSelectionStarted
                    }
                  />
                ) : null}
                {selectedTab === 'hotel' ? (
                  <ResponsiveListingBox
                    setIsRedirecting={setIsRedirecting}
                    detail={false}
                    startDate={startDate}
                    endDate={endDate}
                    hotelRoomsList={hotelRoomsList}
                    isModalVisible={isShowModal || isHotelDateSelected}
                    toggleIsModalVisible={() => setIsShowModal((val) => !val)}
                    hideModal={() => {
                      setIsShowModal(false);
                      setHotelDateSelection(false);
                    }}
                    meta={meta}
                    location={location}
                    shouldShallowPush={false}
                    hotelFilters={hotelFilters}
                    isFetching={isFetching}
                    hotels={hotels}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                  />
                ) : null}
              </>
            ) : null}

            {packageTabChanged || hotels?.[0]?.length === 0 ? null : !isError &&
              isDesktop ? (
              <FlexibleDiv top={12}>
                <SortingTabs
                  select='hotel'
                  activeKey={sort.id}
                  data={HOTEL_SORT_DATA}
                  handleTabClick={handleTabClick}
                />
              </FlexibleDiv>
            ) : (!isError && isMobile) || isTablet ? (
              <>
                {packageTabChanged ||
                isFetching ||
                isRedirecting ||
                hotels?.[0]?.length === 0 ||
                flights?.length === 0 ||
                isError ? null : (
                  <FlightTransferSelectionComponent
                    adultCount={adultCount}
                    childCount={childCount}
                    transferItems={transfers}
                    flightItems={flights}
                    selectedDepatureFlight={selectedDepatureFlight}
                    selectedReturnFlight={selectedReturnFlight}
                    selectedDepatureTransfer={selectedDepatureTransfer}
                    selectedReturnTransfer={selectedReturnTransfer}
                    setSelectedDepatureFlight={setSelectedDepatureFlight}
                    setSelectedDepatureTransfer={setSelectedDepatureTransfer}
                    setSelectedReturnFlight={setSelectedReturnFlight}
                    setSelectedReturnTransfer={setSelectedReturnTransfer}
                    isListing={true}
                  />
                )}
              </>
            ) : null}

            {loyaltyType && !isStickySearch && !isKocAilemValidate ? (
              <KocAilemBanner hotelListing={true} />
            ) : null}

            {(isError ||
              hotels?.[0]?.length === 0 ||
              hotels?.[0] === undefined) &&
            !isFetching ? (
              <NotFoundWrapper>
                <NotFound
                  iconSize={124}
                  label='Aradığınız kriterlerde otel bulunamadı, lütfen tekrar deneyin'
                />
              </NotFoundWrapper>
            ) : null}

            {isFetching ||
            currentPage !==
              Number(query.page === undefined ? 1 : query.page) ? (
              <SkeletonItemsWrapper>
                {Array(9)
                  .fill(0)
                  .map((_, index) => (
                    <HotelCardSkeleton key={index} />
                  ))}
              </SkeletonItemsWrapper>
            ) : null}
            <>
              {!packageTabChanged ||
              (packageTabChanged &&
                !query.dep &&
                !query.in &&
                !query.out &&
                selectedTab === 'package') ? (
                <>
                  {hotels &&
                  hotels?.length > 0 &&
                  !isError &&
                  hotels?.[0]?.length > 0 ? (
                    <>
                      {currentPage > 1 ? (
                        <div
                          id='listing-cancellation-guarantee-banner'
                          style={{
                            width: '100%',
                            marginBottom: '10px',
                          }}
                        />
                      ) : null}
                      <ListingItemContainer>
                        <>
                          {hotels[hotels.length - 1]?.map((item, pageIndex) => (
                            <Fragment key={pageIndex}>
                              <IsInViewPort
                                cssAll='unset'
                                key={item.id}
                                onViewVisible={() => null}
                              >
                                <ListingItemCardWrapper>
                                  <ListingItem
                                    badge={item.features}
                                    activeKey={sort.id}
                                    boardTypes={item.boardTypes}
                                    isDate={startDate ? true : false}
                                    hotelListing={true}
                                    seturExecutive={item.seturExecutive}
                                    image={
                                      item.image
                                        ? item.image
                                        : '/static/images/setur-static.jpg'
                                    }
                                    adultCount={adultCount}
                                    boardType={item.boardType}
                                    checkInDate={query.in}
                                    checkOutDate={query.out}
                                    childCount={childCount}
                                    domestic={item.domestic}
                                    onGoToHotelDetailClick={() => {
                                      packageListAndClick(
                                        [item],
                                        query.in,
                                        query.out,
                                        adultCount,
                                        childCount,
                                        sort,
                                        hotelFilters,
                                        query,
                                        true
                                      );
                                    }}
                                    expedia={item.expedia}
                                    fallBackImage='/static/images/setur-static.jpg'
                                    features={[]}
                                    highlights={item.highlights}
                                    id={item.id}
                                    location={item.location}
                                    name={item.name}
                                    openDatePicker={toggleDateSelectionModal}
                                    pricing={item.pricing}
                                    refundable={false} // removing the free cancellation for package items
                                    score={item.score}
                                    status={item.status}
                                    suggests={item.suggests}
                                    themes={item.themes}
                                    url={
                                      item.url
                                        ? updateRoomParamInUrl(item.url)
                                        : ''
                                    }
                                    installments={item.installments}
                                    images={item.images}
                                    selectedDepatureFlight={
                                      selectedDepatureFlight
                                    }
                                    selectedReturnFlight={selectedReturnFlight}
                                    selectedDepatureTransfer={
                                      selectedDepatureTransfer
                                    }
                                    selectedReturnTransfer={
                                      selectedReturnTransfer
                                    }
                                  />
                                </ListingItemCardWrapper>
                              </IsInViewPort>
                              {isDesktop ? (
                                <>
                                  {pageIndex === hotelSliceCount - 1 &&
                                  currentPage === 1 &&
                                  campaignBanner?.images?.[0]?.url &&
                                  bannerImage
                                    ? renderHeroCampaigns()
                                    : null}
                                </>
                              ) : isMobile || isTablet ? (
                                <>
                                  {pageIndex === hotelSliceCount - 1 &&
                                  currentPage === 1 &&
                                  campaignBanner?.responsiveImages?.[0]?.url &&
                                  bannerImage
                                    ? renderHeroCampaigns()
                                    : null}
                                </>
                              ) : null}
                            </Fragment>
                          ))}
                          {hotelSliceCount > meta.totalCount &&
                          campaignBanner &&
                          bannerImage
                            ? renderHeroCampaigns()
                            : null}
                        </>
                      </ListingItemContainer>
                    </>
                  ) : null}
                </>
              ) : (
                <ResearchContainer>
                  <ResearchWrapper>
                    <CustomIcon
                      icon={InfoIcon}
                      width={40}
                      height={40}
                      paletteColor='Blue600'
                    />
                    <Typography
                      variant='caption1'
                      weight='Regular'
                      color='Blue600'
                    >
                      {selectedTab === 'package'
                        ? 'Güncel paket fiyatlarını görüntüleyebilmek için lütfen yeniden arama yapınız.'
                        : 'Otel fiyatlarını görüntüleyebilmek için lütfen yeniden arama yapınız.'}
                    </Typography>
                  </ResearchWrapper>
                </ResearchContainer>
              )}
            </>

            {isRedirecting ? <GeneralLoader /> : null}
            {hotels?.length < meta.totalCount &&
            meta.totalPages > 1 &&
            !isError &&
            (!packageTabChanged ||
              (packageTabChanged &&
                !query.dep &&
                !query.in &&
                !query.out &&
                selectedTab === 'package')) ? (
              <Pagination
                totalItemCount={meta.totalCount}
                currentPage={query.page ? +query.page : 1}
                totalPages={meta.totalPages}
                searchTitle={actionPageLocationLabel}
                type='hotel'
                onPageChange={setCurrentPage}
              />
            ) : null}
          </ItemsRowWrapper>
        </main>
      </ListWrapper>
      {meta?.description || (metaInfo?.description && currentPage == 1) ? (
        <SeoText text={metaInfo?.content || ''} />
      ) : null}
      {loyaltyType && !isKocAilemValidate ? (
        <LoyaltyController loyaltyType={loyaltyType} refetch={refetch} />
      ) : null}
    </>
  );
};

export default PackageListing;
